import { prerender, query } from "$app/server";
import { getAllProjectsData } from "$lib/project-data";
import { getDictionary, translate } from "../../routes/api/utils";
import { z } from "zod";

const getFilteredAndSortedProjects = (
  lang: string,
  searchQuery: string,
  pinnedOnly: boolean,
) => {
  const dict = getDictionary(lang);

  let projects = getAllProjectsData()
    .map((project) => ({
      ...project,
      key: project.id,
      translatedTitle: String(
        translate(dict, `projects.items.${project.id}.title`),
      ),
      translatedDescription: String(
        translate(dict, `projects.items.${project.id}.description`),
      ),
      translatedImgAlt: String(
        translate(dict, `projects.items.${project.id}.imgAlt`),
      ),
    }))
    .sort((a, b) =>
      String(
        [a.translatedTitle, a.translatedDescription, ...a.badges].join(" "),
      ).localeCompare(
        String(
          [b.translatedTitle, b.translatedDescription, ...b.badges].join(" "),
        ),
      ),
    );

  let filteredProjects = (() => {
    const searchValue = searchQuery;

    if (!searchValue.trim()) {
      return projects;
    }

    const queryTokens = searchValue.toLowerCase().split(/\s+/).filter(Boolean);
    const q = searchValue.toLowerCase();

    return projects
      .map((project) => {
        let score = 0;
        const title = project.translatedTitle.toLowerCase();
        const description = project.translatedDescription.toLowerCase();
        const badges = project.badges.map((b) => b.toLowerCase());
        const fullText = `${title} ${description} ${badges.join(" ")}`;

        const allTokensMatch = queryTokens.every((token) =>
          fullText.includes(token),
        );

        if (allTokensMatch) {
          if (title.includes(q)) score += 100;
          else if (badges.join(" ").includes(q)) score += 50;
          else if (description.includes(q)) score += 20;

          queryTokens.forEach((token) => {
            if (title.includes(token)) score += 10;
            if (badges.some((b) => b.includes(token))) score += 5;
            if (description.includes(token)) score += 1;
          });
        }

        return { ...project, _score: score };
      })
      .filter((project) => project._score > 0)
      .sort((a, b) => {
        if (b._score !== a._score) {
          return b._score - a._score;
        }
        return String(
          [a.translatedTitle, a.translatedDescription, ...a.badges].join(" "),
        ).localeCompare(
          String(
            [b.translatedTitle, b.translatedDescription, ...b.badges].join(" "),
          ),
        );
      });
  })();
  if (pinnedOnly) {
    filteredProjects = filteredProjects.filter((project) => project.pinned);
  }
  return filteredProjects.map(({ _score, ...project }: any) => project);
};

export const getProjects = prerender(
  z.object({
    lang: z.string(),
    searchQuery: z.string(),
    pinnedOnly: z.boolean(),
  }),
  async ({ lang, searchQuery, pinnedOnly }) => {
    return getFilteredAndSortedProjects(lang, searchQuery, pinnedOnly);
  },
);

export const searchProjects = query(
  z.object({
    lang: z.string(),
    searchQuery: z.string(),
    pinnedOnly: z.boolean(),
  }),
  async ({ lang, searchQuery, pinnedOnly }) => {
    return getFilteredAndSortedProjects(lang, searchQuery, pinnedOnly);
  },
);
